import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import { Download, Eye, Search, Send } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Badge } from '@/components/ui/badge'
import { PaginatedTabTable } from '@/components/shared/PaginatedTabTable'
import { LoadingOverlay } from '@/components/shared'
import { TruncatedTextTooltip } from '@/components/shared/TruncatedTextTooltip'
import { SendReportDialog } from '@/components/jobs/SendReportDialog'
import { BulkSendClientReportsDialog } from '@/components/clients/BulkSendClientReportsDialog'
import {
  ClientTabSection,
  clientTabActionBtnClass,
  clientTabActionsCellClass,
  clientTabActionsColClass,
  clientTabOutlineBtnClass,
  clientTabSearchInputClass,
  clientTabFirstTdClass,
} from '@/components/clients/tabs/ClientTabSection'
import { useClientReports } from '@/hooks/useClients'
import { usePermissions } from '@/hooks/usePermissions'
import { useListParams } from '@/hooks/useListParams'
import { clientsApi } from '@/services/clients'
import { jobsApi } from '@/services/jobs'
import { getApiErrorMessage } from '@/services/api'
import { toast } from '@/components/ui/toast'
import { formatDate } from '@/lib/format'
import { cn } from '@/lib/utils'
import { downloadUrl } from '@/lib/download'
import type { ClientReport } from '@/types'

const orangeActionBtnClass = cn(
  clientTabActionBtnClass,
  'border border-transparent p-[7px] hover:border-[#ff5a1f] hover:text-[#ff5a1f]'
)

// 2.4.10 Client details — reports tab.
export function ReportsTab({
  clientId,
  clientEmail,
  onOpenJobReport,
}: {
  clientId: string
  clientEmail: string
  onOpenJobReport?: (jobId: string) => void
}) {
  const { t } = useTranslation(['clients', 'jobs'])
  const navigate = useNavigate()
  const { canEdit } = usePermissions()
  const canSendReports = canEdit('agenda')
  const { page, setPage, limit, setLimit, search, onSearchChange, query } =
    useListParams()
  const { data, isLoading, isError } = useClientReports(clientId, query)

  const [sendJobId, setSendJobId] = useState<string | null>(null)
  const [bulkSendOpen, setBulkSendOpen] = useState(false)
  const [exportingAll, setExportingAll] = useState(false)
  const [exportingReportId, setExportingReportId] = useState<string | null>(
    null
  )

  const rows = data?.data ?? []
  const totalReports = data?.metadata?.total ?? rows.length
  const openJobReport = (jobId: string) => {
    if (onOpenJobReport) onOpenJobReport(jobId)
    else navigate(`/calendar?job=${jobId}&tab=reports`)
  }

  const exportReport = async (jobId: string) => {
    setExportingReportId(jobId)
    try {
      const res = await jobsApi.reportPdf(jobId)
      if (!res.data?.url) throw new Error(t('shared:exportDialog.error'))
      downloadUrl(res.data.url, res.data.filename)
      toast({ variant: 'success', title: t('shared:exportDialog.success') })
    } catch (e) {
      toast({
        variant: 'destructive',
        title: getApiErrorMessage(e, t('reports.pdfError')),
      })
    } finally {
      setExportingReportId(null)
    }
  }

  const exportAll = async () => {
    if (totalReports === 0) {
      toast({ variant: 'destructive', title: t('reports.exportAllEmpty') })
      return
    }
    setExportingAll(true)
    try {
      const res = await clientsApi.exportAllReports(clientId, query)
      if (res.data?.url) downloadUrl(res.data.url, res.data.filename)
      toast({ variant: 'success', title: t('reports.exportAllSuccess') })
    } catch (e) {
      toast({
        variant: 'destructive',
        title: getApiErrorMessage(e, t('reports.exportAllError')),
      })
    } finally {
      setExportingAll(false)
    }
  }

  return (
    <ClientTabSection
      title={t('detail.tabs.reports')}
      toolbar={
        <div className='flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-end'>
          <div className='flex flex-col gap-4 sm:flex-row sm:items-center'>
            <div className='relative min-w-0 flex-1'>
              <Search className='pointer-events-none absolute left-4 top-1/2 size-4 -translate-y-1/2 text-[#3d434e]' />
              <Input
                value={search}
                onChange={e => onSearchChange(e.target.value)}
                placeholder={t('reports.searchPlaceholder')}
                maxLength={50}
                className={clientTabSearchInputClass}
              />
            </div>
            <div className='flex flex-wrap gap-4'>
              <Button
                type='button'
                variant='outline'
                className={clientTabOutlineBtnClass}
                disabled={exportingAll || totalReports === 0}
                onClick={exportAll}
              >
                {t('reports.exportAll')}
              </Button>
              {canSendReports && (
                <Button
                  type='button'
                  variant='outline'
                  className={clientTabOutlineBtnClass}
                  disabled={totalReports === 0}
                  onClick={() => setBulkSendOpen(true)}
                >
                  {t('reports.sendAll')}
                </Button>
              )}
            </div>
          </div>
        </div>
      }
    >
      <PaginatedTabTable<ClientReport>
        clientHistoryStyle
        data={data}
        isLoading={isLoading}
        isError={isError}
        emptyMessage={t('reports.empty')}
        errorMessage={t('reports.error')}
        page={page}
        pageSize={limit}
        onPageChange={setPage}
        onPageSizeChange={setLimit}
        rowKey={r => r.id}
        onRowClick={r => openJobReport(r.job_id)}
        columns={[
          {
            header: t('reports.columns.jobId'),
            cellClassName: clientTabFirstTdClass,
            cell: r => (
              <div className='text-sm leading-[1.4]'>
                {r.job_reference ?? r.job_id.slice(0, 8)}
              </div>
            ),
          },
          {
            header: t('reports.columns.scheduledDate'),
            cell: r => (
              <span className='text-sm font-normal leading-[1.4] text-[#171a20]'>
                {formatDate(r.scheduled_date)}
              </span>
            ),
          },
          {
            header: t('reports.columns.serviceCategory'),
            className: 'text-center',
            cell: r => (
              <span className='text-sm font-normal leading-[1.4] text-[#171a20]'>
                {r.service_type ?? '-'}
              </span>
            ),
          },
          {
            header: t('reports.columns.leadTechnician'),
            cell: r => (
              <span className='text-sm font-normal leading-[1.4] text-[#171a20]'>
                {r.lead_technician_name ?? '-'}
              </span>
            ),
          },
          {
            header: t('reports.columns.reportStatus'),
            className: 'text-center',
            cell: r => (
              <Badge
                variant='outline'
                className={cn(
                  'rounded-lg border-0 px-1.5 py-1 text-xs font-medium leading-4',
                  r.status === 'completed'
                    ? 'bg-[#d1fae5] text-[#065f46]'
                    : 'bg-[#f4f5f8] text-[#171a20]'
                )}
              >
                {r.status === 'completed'
                  ? t('reports.completed')
                  : t('reports.pending')}
              </Badge>
            ),
          },
          {
            header: t('list.columns.actions'),
            className: clientTabActionsColClass,
            cell: (r, rowIndex) => {
              const actionButtonClass = cn(
                orangeActionBtnClass,
                rowIndex % 2 === 0 ? 'bg-[#f5f6fa]' : 'bg-[#fbfbfc]'
              )

              return (
                <div
                  className={clientTabActionsCellClass}
                  onClick={e => e.stopPropagation()}
                >
                  <TruncatedTextTooltip
                    alwaysShow
                    closeOnPointerDown
                    className='inline-flex shrink-0'
                    content={t('reports.actions.view')}
                    delayDuration={0}
                  >
                    <button
                      type='button'
                      className={actionButtonClass}
                      aria-label={t('reports.actions.view')}
                      onClick={() => openJobReport(r.job_id)}
                    >
                      <Eye className='h-4 w-4' />
                    </button>
                  </TruncatedTextTooltip>
                  <TruncatedTextTooltip
                    alwaysShow
                    closeOnPointerDown
                    className='inline-flex shrink-0'
                    content={t('reports.actions.download')}
                    delayDuration={0}
                  >
                    <button
                      type='button'
                      className={actionButtonClass}
                      aria-label={t('reports.actions.download')}
                      disabled={exportingReportId === r.job_id}
                      onClick={() => exportReport(r.job_id)}
                    >
                      <Download className='h-4 w-4' />
                    </button>
                  </TruncatedTextTooltip>
                  {canSendReports && (
                    <TruncatedTextTooltip
                      alwaysShow
                      closeOnPointerDown
                      className='inline-flex shrink-0'
                      content={t('reports.actions.send')}
                      delayDuration={0}
                    >
                      <button
                        type='button'
                        className={actionButtonClass}
                        aria-label={t('reports.actions.send')}
                        onClick={() => setSendJobId(r.job_id)}
                      >
                        <Send className='h-4 w-4' />
                      </button>
                    </TruncatedTextTooltip>
                  )}
                </div>
              )
            },
          },
        ]}
      />

      {sendJobId && (
        <SendReportDialog
          jobId={sendJobId}
          defaultEmail={clientEmail}
          defaultBody={t('jobs:sendReport.defaultBody')}
          onClose={() => setSendJobId(null)}
        />
      )}

      {bulkSendOpen && (
        <BulkSendClientReportsDialog
          onClose={() => setBulkSendOpen(false)}
          clientId={clientId}
          clientEmail={clientEmail}
          reportCount={totalReports}
          search={search || undefined}
        />
      )}
      <LoadingOverlay open={exportingAll || exportingReportId !== null} />
    </ClientTabSection>
  )
}
